import { useEffect, useRef, useState } from 'react';
import { centavos, formatMoney, formatQuantity, type Centavos, type Milli } from '@abastece/shared';
import { useT } from './i18n/index.js';
import {
  abrirCanal,
  hayCanal,
  type EstadoPantalla,
  type LineaPantalla,
  type MensajePantalla,
} from './lib/pantalla-cliente.js';
import { Ico } from './ui/Iconos.js';
import { Miniatura, MiniaturaIcono } from './ui/Miniatura.js';

/**
 * The picture beside each line, sized by the window like everything else here. Drawn at the
 * largest size so a pictogram's glyph is big enough in it, then fitted to the screen.
 */
const FOTO = 'size-[clamp(3.5rem,6vw,6.5rem)]! rounded-2xl!';

/** How long «Gracias» stays up when nobody starts the next sale. */
const GRACIAS_MS = 30_000;

/**
 * The screen the customer sees: a second window, on a monitor turned toward the counter.
 *
 * Mounted by `main.tsx` in place of the app, not inside it. It has no device to register, nobody
 * to sign in and no PIN to ask for, because the lock would put a PIN pad in front of the
 * customer; it does not sync, and it cannot go anywhere. All it knows is what the till in the
 * other window tells it (see `lib/pantalla-cliente.ts`).
 *
 * Sized in viewport units, so the same page reads across the counter on a 24-inch monitor and
 * still fits a seven-inch one.
 */
export function PantallaCliente() {
  const t = useT();
  const [negocio, setNegocio] = useState('');
  const [estado, setEstado] = useState<EstadoPantalla>({ tipo: 'espera' });
  /** A till has spoken since this window opened. */
  const [conectada, setConectada] = useState(false);
  const [completa, setCompleta] = useState(() => Boolean(document.fullscreenElement));

  useEffect(() => {
    if (!hayCanal()) return;
    const canal = abrirCanal();
    canal.onmessage = (e: MessageEvent<MensajePantalla>) => {
      if (e.data?.tipo !== 'estado') return;
      setConectada(true);
      setNegocio(e.data.negocio);
      setEstado(e.data.estado);
    };
    // Opened halfway through a sale, it asks rather than waiting for the next line.
    canal.postMessage({ tipo: 'hola' } satisfies MensajePantalla);
    return () => canal.close();
  }, []);

  useEffect(() => {
    if (estado.tipo !== 'gracias') return;
    const reloj = window.setTimeout(() => setEstado({ tipo: 'espera' }), GRACIAS_MS);
    return () => window.clearTimeout(reloj);
  }, [estado]);

  useEffect(() => {
    const alCambiar = () => setCompleta(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', alCambiar);
    return () => document.removeEventListener('fullscreenchange', alCambiar);
  }, []);

  /*
   * A monitor nobody touches is one the computer turns off after a few minutes, halfway through
   * the day. The lock is let go whenever the window is hidden, so it is asked for again each
   * time it comes back.
   */
  useEffect(() => {
    let candado: WakeLockSentinel | null = null;
    const pedir = async () => {
      if (document.visibilityState !== 'visible' || !('wakeLock' in navigator)) return;
      try {
        candado = await navigator.wakeLock.request('screen');
      } catch {
        // Refused, or the battery is low: the screen just follows the computer's own settings.
      }
    };
    void pedir();
    document.addEventListener('visibilitychange', pedir);
    return () => {
      document.removeEventListener('visibilitychange', pedir);
      void candado?.release();
    };
  }, []);

  // The till reloads itself when a new version is accepted; this window has nobody to press
  // the button, so it follows along when the new version takes over.
  useEffect(() => {
    const sw = navigator.serviceWorker;
    if (!sw) return;
    const recargar = () => location.reload();
    sw.addEventListener('controllerchange', recargar);
    return () => sw.removeEventListener('controllerchange', recargar);
  }, []);

  const titulo = t('pantalla.titulo');
  useEffect(() => {
    document.title = titulo;
  }, [titulo]);

  return (
    // In full screen the pointer goes: a mouse left resting on the customer's monitor is a
    // cursor in the middle of their total.
    <div className={`flex h-dvh flex-col overflow-hidden ${completa ? 'cursor-none' : ''}`}>
      {!completa && <ParaQuienAtiende conectada={conectada} />}
      {estado.tipo === 'venta' && estado.lineas.length > 0 ? (
        <Venta
          lineas={estado.lineas}
          envio={estado.envio}
          redondeo={estado.redondeo}
          total={estado.total}
          cobrando={estado.cobrando}
          ahorro={estado.ahorro}
        />
      ) : estado.tipo === 'gracias' ? (
        <Gracias total={estado.total} cambio={estado.cambio} ahorro={estado.ahorro} />
      ) : (
        <Espera negocio={negocio} />
      )}
    </div>
  );
}

/**
 * The strip for whoever set the screen up, gone once it is full screen: where the window goes,
 * the button to fill it, and a word when no till has spoken yet.
 */
function ParaQuienAtiende({ conectada }: { conectada: boolean }) {
  const t = useT();
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-slate-800 bg-slate-900 px-5 py-3">
      <p className="min-w-0 flex-1 basis-64 text-sm text-slate-300">
        {t('pantalla.ayuda')}
        {!conectada && <span className="mt-1 block text-amber-300">{t('pantalla.sinCaja')}</span>}
      </p>
      <button
        type="button"
        className="btn-primario min-h-11 shrink-0 px-4 text-sm"
        onClick={() => void document.documentElement.requestFullscreen().catch(() => {})}
      >
        <Ico icono="pantallaCompleta" className="size-4" />
        {t('pantalla.completa')}
      </button>
    </div>
  );
}

/**
 * The sale as it is rung up: the lines down the left, the total big on the right, or under them
 * on a screen taller than it is wide.
 *
 * The line that just changed is lit and brought into view, so the customer's eye goes to what was
 * just rung up, which is the thing they are checking.
 */
function Venta({
  lineas,
  envio,
  redondeo,
  total,
  cobrando,
  ahorro,
}: {
  lineas: LineaPantalla[];
  /** What the trip costs, already in `total`: said over it, so the customer sees what it is for. */
  envio: Centavos | undefined;
  /** The cash rounding, already in `total`: said over it, so the figure asked for is the one on the screen. */
  redondeo: Centavos | undefined;
  total: Centavos;
  cobrando: boolean;
  /** What the offers on it save, already out of `total`: said over it, in the offer's colour. */
  ahorro: Centavos | undefined;
}) {
  const t = useT();
  const antes = useRef(new Map<string, Milli>());
  const filas = useRef(new Map<string, HTMLLIElement>());
  const [reciente, setReciente] = useState<string | null>(null);

  useEffect(() => {
    const previas = antes.current;
    const cambiada = [...lineas].reverse().find((l) => previas.get(l.clave) !== l.cantidad);
    antes.current = new Map(lineas.map((l) => [l.clave, l.cantidad]));
    if (!cambiada) return;
    setReciente(cambiada.clave);
    filas.current.get(cambiada.clave)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [lineas]);

  return (
    <main className="aparece grid min-h-0 flex-1 grid-rows-[minmax(0,1fr)_auto] landscape:grid-cols-[minmax(0,1fr)_minmax(16rem,38%)] landscape:grid-rows-1">
      <ol className="min-h-0 overflow-y-auto px-[3vw] py-[2vh]">
        {lineas.map((l) => (
          <li
            key={l.clave}
            ref={(el) => {
              if (el) filas.current.set(l.clave, el);
              else filas.current.delete(l.clave);
            }}
            className={`entra flex items-center gap-[2vw] rounded-xl px-[1.5vw] py-[1.4vh] transition-colors ${
              l.clave === reciente ? 'bg-marca-500/12 ring-1 ring-marca-500/30 ring-inset' : ''
            }`}
          >
            {/*
              The picture first, as on the till: across a counter a customer knows their soda by
              its label before they have read «1.5 litros».
            */}
            {l.miniatura ? (
              <Miniatura producto={l.miniatura} tamano="xl" soloLocal className={FOTO} />
            ) : (
              <MiniaturaIcono icono="rapido" tamano="xl" className={FOTO} />
            )}
            <div className="min-w-0 flex-1">
              <p className="text-[clamp(1.2rem,2.4vw,2.75rem)] leading-tight font-semibold text-balance wrap-break-word">
                {l.nombre}
              </p>
              <p className="tabular mt-[0.6vh] text-[clamp(0.95rem,1.6vw,1.75rem)] text-slate-400">
                {formatQuantity(l.cantidad)} × {l.precio > 0 ? formatMoney(l.precio, { symbol: false }) : '—'}
                {l.unidad && ` ${t('precios.porUnidad', { unidad: l.unidad })}`}
              </p>
              {/* So many for fewer: so, and how many are free. */}
              {l.lleva && (
                <p className="tabular mt-[0.4vh] text-[clamp(0.9rem,1.5vw,1.6rem)] font-semibold text-teal-300" data-pantalla-oferta>
                  {l.lleva.gratis > 0 ? t('pantalla.llevaGratis', { nombre: l.lleva.nombre, n: formatQuantity(l.lleva.gratis) }) : l.lleva.nombre}
                </p>
              )}
              {/* At an offer: so, and the price it is not, struck. */}
              {l.precioRegular !== undefined && (
                <p className="tabular mt-[0.4vh] text-[clamp(0.9rem,1.5vw,1.6rem)] font-semibold text-teal-300" data-pantalla-oferta>
                  {t('pantalla.oferta')} <s className="font-normal text-slate-500">{formatMoney(l.precioRegular, { symbol: false })}</s>
                </p>
              )}
            </div>
            <p
              key={l.subtotal}
              className="late tabular shrink-0 text-[clamp(1.4rem,2.9vw,3.25rem)] font-bold tracking-tight"
            >
              {l.precio > 0 ? formatMoney(l.subtotal, { symbol: false }) : '—'}
            </p>
          </li>
        ))}
      </ol>
      {/*
        The total is sized by the width of its own panel (`cqw`), not the window's, so it stays on
        one line: at the window's scale «C$ 184.64» broke after the «C$». Wide enough for
        «12,345.00»; the currency smaller than the figure, the way a price tag prints it.
      */}
      <aside className="@container flex flex-col justify-center gap-[1vh] border-t border-slate-800 bg-slate-900 px-[4vw] py-[3vh] landscape:border-t-0 landscape:border-l">
        {envio !== undefined && envio > 0 && (
          <p className="tabular mb-[1vh] flex items-baseline justify-between gap-4 text-[clamp(1rem,1.9vw,2.1rem)] text-slate-300" data-pantalla-envio>
            <span>{t('envio.campo')}</span>
            <span>{formatMoney(envio)}</span>
          </p>
        )}
        {ahorro !== undefined && ahorro > 0 && (
          <p className="tabular mb-[1vh] flex items-baseline justify-between gap-4 text-[clamp(1rem,1.9vw,2.1rem)] font-semibold text-teal-300" data-pantalla-ahorro>
            <span>{t('pantalla.ahorra')}</span>
            <span>{formatMoney(ahorro)}</span>
          </p>
        )}
        {redondeo !== undefined && redondeo !== 0 && (
          <p className="tabular mb-[1vh] flex items-baseline justify-between gap-4 text-[clamp(1rem,1.9vw,2.1rem)] text-slate-300" data-pantalla-redondeo>
            <span>{t('cobro.redondeo')}</span>
            <span>
              {redondeo > 0 ? '+' : '−'} {formatMoney(centavos(Math.abs(redondeo)))}
            </span>
          </p>
        )}
        <p className="text-[clamp(1.1rem,2.2vw,2.5rem)] text-slate-400">
          {t(cobrando ? 'pantalla.aPagar' : 'comun.total')}
        </p>
        <p
          key={total}
          className="late tabular text-[clamp(2.25rem,17cqw,9rem)] leading-none font-bold tracking-tight whitespace-nowrap"
        >
          <span className="mr-[0.2em] align-[0.6em] text-[0.4em] font-semibold text-slate-300">C$</span>
          {formatMoney(total, { symbol: false })}
        </p>
      </aside>
    </main>
  );
}

/** Paid: thanks, what it came to, what the offers saved, and the change to hand back, which is what they check next. */
function Gracias({ total, cambio, ahorro }: { total: Centavos; cambio: Centavos; ahorro: Centavos | undefined }) {
  const t = useT();
  return (
    <main className="aparece flex flex-1 flex-col items-center justify-center gap-[3vh] px-[5vw] text-center">
      <span className="brinca chip chip-esmeralda size-[clamp(4rem,9vw,8rem)] rounded-3xl">
        <Ico icono="listo" className="size-1/2" />
      </span>
      <h1 className="text-[clamp(2.25rem,6vw,6.5rem)] leading-tight font-bold text-balance">{t('pantalla.gracias')}</h1>
      <p className="tabular text-[clamp(1.25rem,2.8vw,3rem)] text-slate-300">
        {t('comun.total')} {formatMoney(total)}
      </p>
      {ahorro !== undefined && ahorro > 0 && (
        <p className="tabular text-[clamp(1.1rem,2.4vw,2.6rem)] font-semibold text-teal-300" data-pantalla-ahorro>
          {t('pantalla.ahorro', { monto: formatMoney(ahorro) })}
        </p>
      )}
      {cambio > 0 && (
        <div className="rounded-3xl bg-amber-500/10 px-[5vw] py-[2.5vh] ring-1 ring-amber-500/30 ring-inset">
          <p className="text-[clamp(1.1rem,2.4vw,2.5rem)] text-amber-200">{t('pantalla.vuelto')}</p>
          <p className="tabular text-[clamp(2.5rem,6.5vw,7rem)] leading-none font-bold text-amber-100">
            {formatMoney(cambio)}
          </p>
        </div>
      )}
    </main>
  );
}

/** Between sales, and before any till has spoken: the shop's name and a welcome. */
function Espera({ negocio }: { negocio: string }) {
  const t = useT();
  return (
    <main className="aparece flex flex-1 flex-col items-center justify-center gap-[2.5vh] px-[5vw] text-center">
      <span className="chip chip-marca size-[clamp(4rem,9vw,8rem)] rounded-3xl">
        <Ico icono="negocio" className="size-1/2" />
      </span>
      {negocio && (
        <h1 className="text-[clamp(2rem,5.5vw,6rem)] leading-tight font-bold text-balance">{negocio}</h1>
      )}
      <p className="text-[clamp(1.25rem,3vw,3.25rem)] text-slate-400">{t('pantalla.bienvenido')}</p>
    </main>
  );
}
